import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import BaseDialog from 'core/components/base-dialog/base-dialog';
import Label from 'core/components/label/label';
import Button from 'core/components/button/button';
import { FormField } from 'core/components/form';
import RunArtifacts from './run-artifacts';
import { COUNT_LABELS, REP_UPDATE_TYPE_OPTIONS } from './helpers';
import useStyles from './approve-and-apply-dialog.styles';

/**
 * The single operator gesture. Approval resumes the parked machine, so this dialog does two jobs at
 * once: it restates the blast radius the operator authorises, and it collects what the snapshot
 * deliberately does not carry — the new producer and which rep fields to update. Those two inputs
 * used to live in a separate apply dialog, which no longer exists.
 *
 * The producer is OPTIONAL, but "optional" is expressed as an affirmative choice rather than an
 * empty box: one username stamps the WHOLE run, so a silently-blank field is the kind of default
 * that migrates a book to nobody by accident.
 */
const validationSchema = Yup.object().shape({
  leaveProducersUnchanged: Yup.boolean(),
  repUsername: Yup.string()
    .trim()
    .when('leaveProducersUnchanged', {
      is: false,
      then: Yup.string().trim().required('Enter a producer username, or choose "Leave producers unchanged".'),
      otherwise: Yup.string().trim()
    }),
  updateType: Yup.string()
    .oneOf(REP_UPDATE_TYPE_OPTIONS.map((option) => option.id))
    .required()
});

const updateTypeLabel = (id) => REP_UPDATE_TYPE_OPTIONS.find((option) => option.id === id)?.value ?? id;

const ApproveAndApplyDialog = ({ open, onClose, onConfirm, run, error }) => {
  const classes = useStyles();
  const counts = run?.counts ?? {};

  const handleSubmit = useCallback(
    async (values, { setSubmitting }) => {
      try {
        // Blank is not a signal any more — the checkbox is. Send null only when it is set.
        await onConfirm({
          repUsername: values.leaveProducersUnchanged ? null : values.repUsername.trim(),
          updateType: values.updateType
        });
      } finally {
        setSubmitting(false);
      }
    },
    [onConfirm]
  );

  return (
    <BaseDialog className={classes.modal} onClose={onClose} open={open}>
      <div className={classes.container}>
        <Label className={classes.mainLabel} type="green75Big">
          Approve &amp; apply this AOR run?
        </Label>
        <Label type="subtitle1Regular">
          {`Approving resumes this run immediately and migrates the records below for AOR request ${run?.aorRequestId}. There is no separate apply step.`}
        </Label>

        <div>
          {Object.entries(COUNT_LABELS).map(([key, label]) => (
            <Label key={key} type="infoValue">
              {`${label}: ${counts[key] ?? 0}`}
            </Label>
          ))}
        </div>

        <Label type="infoLabel">
          {`Policies are counted with no status filter — ${counts.policiesInForce ?? 0} of ${
            counts.policies ?? 0
          } are currently in force.`}
        </Label>

        <Label type="infoLabel">Check the records before approving:</Label>
        <RunArtifacts previewTaskRunId={run?.previewTaskRunId} />

        <Formik
          initialValues={{ leaveProducersUnchanged: true, repUsername: '', updateType: 'sales_rep' }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values }) => (
            <Form className={classes.form}>
              <FormField
                name="leaveProducersUnchanged"
                type="checkbox"
                label="Leave producers unchanged (affinity and NRAT only)"
                mode="light"
                disabled={isSubmitting}
              />
              <FormField
                name="repUsername"
                type="string"
                label="New producer staff username"
                mode="light"
                disabled={isSubmitting || values.leaveProducersUnchanged}
              />
              <FormField
                name="updateType"
                type="select"
                label="Producer fields to update"
                mode="light"
                options={REP_UPDATE_TYPE_OPTIONS}
                disabled={isSubmitting || values.leaveProducersUnchanged}
              />
              <Label type="infoLabel">
                One username stamps the WHOLE run — for books whose policies belong to different agents, leave producers
                unchanged and use Update Policy Rep (Bulk) per policy. An endorsement whose preview changes the price is
                NOT confirmed — it lands in the report as price_change for manual handling.
              </Label>

              <div className={classes.summary}>
                <Label type="infoLabel">On approval</Label>
                <Label type="infoValue">
                  {values.leaveProducersUnchanged
                    ? 'No producer change'
                    : `Producer → ${values.repUsername.trim() || '—'} (${updateTypeLabel(values.updateType)})`}
                </Label>
              </div>

              {/* A 409 here is expected (already approved, mappings changed, window closed) and its
                  wording tells the operator what to do next, so it belongs in the dialog. */}
              {error && (
                <Label type="infoValue" className={classes.errorText}>
                  {error}
                </Label>
              )}

              <div className={classes.buttonContainer}>
                <Button
                  className={classes.buttons}
                  variant="contained"
                  color="primary"
                  type="submit"
                  loading={isSubmitting}
                >
                  Yes, approve &amp; apply
                </Button>
                <Button
                  className={classes.buttons}
                  variant="contained"
                  color="secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </BaseDialog>
  );
};

ApproveAndApplyDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  run: PropTypes.shape({
    aorRequestId: PropTypes.string,
    previewTaskRunId: PropTypes.string,
    counts: PropTypes.object
  }).isRequired,
  error: PropTypes.string
};

ApproveAndApplyDialog.defaultProps = {
  error: null
};

export default ApproveAndApplyDialog;
